/**
 * Voice Memo Message
 *
 * Renders a user voice memo with audio playback and transcript segments.
 *
 * Primary responsibilities:
 * - Resolve persisted voice memo audio URLs
 * - Keep transcript highlighting synchronized with playback
 * - Render pending and failed transcription states without losing audio
 *
 * Dependencies:
 * - components/ai-elements/transcription
 * - lib/supabase/signed-url-manager
 */

'use client'

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Mic, Pencil } from 'lucide-react'

import {
  AudioPlayer,
  AudioPlayerControlBar,
  AudioPlayerDurationDisplay,
  AudioPlayerElement,
  AudioPlayerPlayButton,
  AudioPlayerTimeDisplay,
  AudioPlayerTimeRange
} from '@/components/ai-elements/audio-player'
import {
  Transcription,
  TranscriptionSegment
} from '@/components/ai-elements/transcription'
import { VoiceMemoWaveform } from '@/components/messages/voice-memo-waveform'
import {
  createTranscriptSegments,
  updateChatVoiceMemoTranscript
} from '@/lib/chat/voice-memo'
import { cn } from '@/lib/utils'
import type { ChatVoiceMemoMetadata } from '@/types/voice-memo'

interface VoiceMemoMessageProps {
  className?: string
  onTranscriptSave?: (transcript: string) => void | Promise<void>
  voiceMemo: ChatVoiceMemoMetadata
}

function getEmptyTranscriptLabel(voiceMemo: ChatVoiceMemoMetadata) {
  if (voiceMemo.transcriptionStatus === 'pending') {
    return 'Transcribing...'
  }

  if (voiceMemo.transcriptionStatus === 'failed') {
    return 'Transcription failed'
  }

  return 'No transcript is available for this voice memo.'
}

// --------------

function readAudioElementDuration(audio: HTMLAudioElement) {
  return Number.isFinite(audio.duration) && audio.duration > 0
    ? audio.duration
    : null
}

// --------------

function getVoiceMemoPlaybackUrl(voiceMemo: ChatVoiceMemoMetadata) {
  const audioPath = voiceMemo.audioPath?.trim()

  if (audioPath) {
    return `/api/voice-notes/audio/${audioPath.split('/').map(encodeURIComponent).join('/')}`
  }

  return voiceMemo.audioUrl ?? ''
}

// --------------

export function VoiceMemoMessage({
  className,
  onTranscriptSave,
  voiceMemo
}: VoiceMemoMessageProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [audioUrl, setAudioUrl] = useState(getVoiceMemoPlaybackUrl(voiceMemo))
  const [currentTime, setCurrentTime] = useState(0)
  const [isEditingTranscript, setIsEditingTranscript] = useState(false)
  const [isSavingTranscript, setIsSavingTranscript] = useState(false)
  const [localTranscript, setLocalTranscript] = useState(voiceMemo.transcript)
  const [transcriptDraft, setTranscriptDraft] = useState(voiceMemo.transcript)
  const [resolvedDurationSeconds, setResolvedDurationSeconds] = useState(
    voiceMemo.durationSeconds
  )
  const pendingSavedTranscriptRef = useRef<string | null>(null)
  const effectiveVoiceMemo =
    localTranscript === voiceMemo.transcript
      ? voiceMemo
      : updateChatVoiceMemoTranscript(voiceMemo, localTranscript)
  const hasTranscript = effectiveVoiceMemo.transcript.trim().length > 0
  const canEditTranscript = Boolean(onTranscriptSave)
  const emptyTranscriptLabel = getEmptyTranscriptLabel(voiceMemo)
  const segments = useMemo(
    () =>
      localTranscript === voiceMemo.transcript
        ? voiceMemo.segments.filter((segment) => segment.text.trim().length > 0)
        : createTranscriptSegments(localTranscript, voiceMemo.durationSeconds),
    [localTranscript, voiceMemo.durationSeconds, voiceMemo.segments, voiceMemo.transcript]
  )

  useEffect(() => {
    const playbackUrl = getVoiceMemoPlaybackUrl(voiceMemo)

    if (playbackUrl) {
      setAudioUrl(playbackUrl)
      return
    }

    setAudioUrl('')
  }, [voiceMemo])

  useEffect(() => {
    setResolvedDurationSeconds(voiceMemo.durationSeconds)
  }, [voiceMemo.durationSeconds])

  useEffect(() => {
    if (!isEditingTranscript) {
      const pendingTranscript = pendingSavedTranscriptRef.current

      if (pendingTranscript && pendingTranscript !== voiceMemo.transcript) {
        return
      }

      pendingSavedTranscriptRef.current = null
      setLocalTranscript(voiceMemo.transcript)
      setTranscriptDraft(voiceMemo.transcript)
    }
  }, [isEditingTranscript, voiceMemo.transcript])

  const seekAudio = (time: number) => {
    setCurrentTime(time)

    if (audioRef.current) {
      audioRef.current.currentTime = time
      void audioRef.current.play().catch(() => {})
    }
  }

  const saveTranscriptDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!onTranscriptSave) return

    setIsSavingTranscript(true)
    try {
      const cleanTranscript = transcriptDraft.trim()
      await onTranscriptSave(cleanTranscript)
      pendingSavedTranscriptRef.current = cleanTranscript
      setLocalTranscript(cleanTranscript)
      setTranscriptDraft(cleanTranscript)
      setIsEditingTranscript(false)
    } finally {
      setIsSavingTranscript(false)
    }
  }

  return (
    <div
      className={cn(
        'not-prose border-border/70 bg-background/95 my-2 w-full max-w-2xl rounded-[2rem] border p-3 shadow-sm',
        className
      )}
    >
      <div className="mb-2 flex min-w-0 items-center gap-3">
        <div className="bg-muted text-foreground flex size-8 shrink-0 items-center justify-center rounded-full">
          <Mic className="size-4" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 overflow-hidden">
          <VoiceMemoWaveform
            className="h-7"
            samples={voiceMemo.waveformSamples}
          />
        </div>
      </div>

      {audioUrl ? (
        <AudioPlayer className="border-border/70 bg-background/70 rounded-full px-3 py-2 shadow-none">
          <AudioPlayerElement
            ref={audioRef}
            contentType={voiceMemo.contentType}
            durationHint={resolvedDurationSeconds}
            src={audioUrl}
            onDurationChange={(event) => {
              const duration = readAudioElementDuration(event.currentTarget)
              if (duration !== null) {
                setResolvedDurationSeconds(duration)
              }
            }}
            onLoadedMetadata={(event) => {
              const duration = readAudioElementDuration(event.currentTarget)
              if (duration !== null) {
                setResolvedDurationSeconds(duration)
              }
            }}
            onTimeUpdate={(event) => {
              const nextCurrentTime = event.currentTarget.currentTime
              setCurrentTime(nextCurrentTime)
              setResolvedDurationSeconds((previous) =>
                typeof previous === 'number' && Number.isFinite(previous)
                  ? Math.max(previous, nextCurrentTime)
                  : nextCurrentTime
              )
            }}
          />
          <AudioPlayerControlBar className="gap-2">
            <AudioPlayerPlayButton />
            <AudioPlayerTimeDisplay className="w-10 shrink-0" />
            <AudioPlayerTimeRange className="min-w-0" />
            <AudioPlayerDurationDisplay className="w-10 shrink-0 text-right" />
          </AudioPlayerControlBar>
        </AudioPlayer>
      ) : (
        <div className="border-border text-muted-foreground rounded-full border border-dashed px-4 py-2 text-sm">
          Audio is unavailable.
        </div>
      )}

      <div className="mt-3 px-1">
        {isEditingTranscript ? (
          <form className="space-y-2" onSubmit={saveTranscriptDraft}>
            <textarea
              aria-label="Edit voice memo transcript"
              autoFocus
              className="border-border bg-background text-foreground focus-visible:ring-ring min-h-24 w-full resize-none rounded-xl border px-3 py-2 text-sm leading-relaxed shadow-sm focus-visible:outline-none focus-visible:ring-2"
              value={transcriptDraft}
              onChange={(event) => setTranscriptDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setTranscriptDraft(effectiveVoiceMemo.transcript)
                  setIsEditingTranscript(false)
                }
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2"
                disabled={isSavingTranscript}
                onClick={() => {
                  setTranscriptDraft(effectiveVoiceMemo.transcript)
                  setIsEditingTranscript(false)
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-ring rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
                disabled={isSavingTranscript}
              >
                Save
              </button>
            </div>
          </form>
        ) : hasTranscript ? (
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <Transcription
                className="italic"
                currentTime={currentTime}
                segments={segments}
                onSeek={seekAudio}
              >
                {(segment, index) => (
                  <TranscriptionSegment
                    key={`${voiceMemo.audioPath ?? voiceMemo.audioUrl}-${index}`}
                    index={index}
                    segment={segment}
                    className="text-muted-foreground hover:text-muted-foreground"
                    style={{ fontStyle: 'italic' }}
                  />
                )}
              </Transcription>
            </div>
            {canEditTranscript ? (
              <button
                type="button"
                aria-label="Edit voice memo transcript"
                className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mt-1 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2"
                onClick={() => setIsEditingTranscript(true)}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <p className="text-muted-foreground min-w-0 flex-1 text-sm italic">
              {emptyTranscriptLabel}
            </p>
            {canEditTranscript ? (
              <button
                type="button"
                aria-label="Edit voice memo transcript"
                className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -mt-1 flex size-8 shrink-0 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2"
                onClick={() => setIsEditingTranscript(true)}
              >
                <Pencil className="size-4" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
